import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Red alert panel that lists budgets with NF emitted but paperwork pending.
 * - Each row has a "sem concorrências" checkbox.
 * - Checking it opens a confirmation modal and finalizes the paperwork server-side
 *   with no_competitors=true, skipping the 2-competitors requirement.
 * - After success, the budget disappears from the list and the parent is told to refresh.
 */
export default function PendingPaperworkAlert({ onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, item: null });

  const load = async () => {
    try {
      const r = await api.get("/paperwork/pending");
      setItems(r.data || []);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const closeWithoutCompetitors = async (p) => {
    setBusyId(p.id);
    try {
      await api.post(`/budgets/${p.id}/paperwork-done`, { no_competitors: true });
      // Download the ZIP for admin to attach
      const r = await api.get(`/budgets/${p.id}/bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pacote_${p.number}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Orçamento Nº ${p.number} finalizado sem concorrências.`);
      setConfirm({ open: false, item: null });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao finalizar");
    } finally {
      setBusyId(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <>
      <div
        className="bg-red-50 border-2 border-red-300 rounded-2xl p-4 card-shadow"
        data-testid="pending-paperwork-alert"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0 animate-pulse">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading text-sm md:text-base font-black text-red-700">
              Papelada pendente — {items.length} orçamento{items.length > 1 ? "s" : ""} aguarda{items.length > 1 ? "m" : ""} finalização
            </p>
            <p className="text-[12px] text-red-700/80 mt-0.5">
              Você emitiu a nota fiscal mas ainda falta gerar concorrências / baixar o pacote completo.
            </p>
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-[11px] font-bold text-red-700 underline hover:text-red-900"
                data-testid="btn-toggle-pending"
              >
                {open ? "Ocultar lista" : "Ver quais"}
              </button>
            </div>
            {open && (
              <ul className="mt-3 space-y-2" data-testid="pending-paperwork-list">
                {items.map((p) => (
                  <li
                    key={p.id}
                    className="bg-white border border-red-200 rounded-xl px-3 py-2"
                    data-testid={`pending-item-${p.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/orcamentos/${p.id}/editar`}
                        className="flex-1 min-w-0 flex items-center justify-between gap-2 hover:bg-red-50 rounded-lg -mx-1 px-1 py-0.5 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-slate-900 truncate">
                            {p.school_name || "—"}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            Orçamento Nº {p.number} • NF {p.nf_number}
                            {p.reasons && p.reasons.length > 0 && (
                              <span className="text-red-700 font-semibold"> • {p.reasons.join(", ")}</span>
                            )}
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-red-600 shrink-0" />
                      </Link>
                    </div>
                    <label
                      className="mt-2 flex items-center gap-2 cursor-pointer select-none text-[11px] font-semibold text-slate-700 hover:text-slate-900"
                      data-testid={`chk-no-comp-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-slate-300 accent-emerald-600"
                        disabled={busyId === p.id}
                        checked={false}
                        onChange={() => setConfirm({ open: true, item: p })}
                      />
                      Este orçamento não terá concorrências
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      <Dialog open={confirm.open} onOpenChange={(o) => !o && setConfirm({ open: false, item: null })}>
        <DialogContent className="max-w-md" data-testid="confirm-no-comp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Finalizar sem concorrências?
            </DialogTitle>
          </DialogHeader>
          {confirm.item && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                Você está marcando o orçamento <b>Nº {confirm.item.number}</b> da escola <b>{confirm.item.school_name}</b> como <b>finalizado sem concorrências</b>.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                O pacote .ZIP vai ser gerado apenas com o orçamento AMC, a nota fiscal e a declaração. Nenhuma concorrência será incluída.
                <br />Esta ação não pode ser desfeita sem suporte.
              </div>
            </div>
          )}
          <DialogFooter className="flex-row gap-2 sm:justify-stretch">
            <Button
              variant="outline"
              onClick={() => setConfirm({ open: false, item: null })}
              className="flex-1"
              data-testid="btn-no-comp-cancel"
            >
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
            <Button
              onClick={() => confirm.item && closeWithoutCompetitors(confirm.item)}
              disabled={busyId !== null}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-no-comp-confirm"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              {busyId ? "Finalizando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
